import {
  Box,
  Checkbox,
  Input,
  Select,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { FC, memo, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { formatNumberType } from "utils/formatNumbers";
import { createUpdateFleetReport, fleetReportsSelectVans } from "actions/fleetReportsAction";
import { fleetStatusConstants, paymentMethods } from "constants/constants";
import { FleetReportData } from "types/FleetReports";
import { FleetReportsTableColumn } from "./tableData";
import Error from "@material-ui/icons/Error";
import moment from "moment";
import { ChipBlock } from "componentsNew/elements/ChipBlock";
import Alert from "@material-ui/lab/Alert";

interface FleetReportRowProps {
  row: FleetReportData;
  isOffBoarded?: boolean;
  isFixed?: boolean;
  columns: FleetReportsTableColumn[];
  classes: any;
}

export const FleetReportRow: FC<FleetReportRowProps> = memo(
  ({ row, isOffBoarded, isFixed, columns, classes }) => {
    const dispatch = useDispatch();
    const [rowData, setRowData] = useState(row);
    const isSelected = useTypeSelectors((state) =>
      state.fleetReports.selectedIdList.some(
        (item) => item.month === row.month && item.year === row.year && item.van === row.van_number,
      ),
    );

    useEffect(() => {
      setRowData(row);
    }, [row]);

    const handleChangeCell = useCallback((columnId: string, value: string | number) => {
      setRowData((prev) => ({ ...prev, [columnId]: value }));
    }, []);

    const handleUpdateCell = useCallback(
      (columnId: string, value: string | number) => {
        setRowData((prev) => ({ ...prev, [columnId]: value }));
        dispatch(createUpdateFleetReport(columnId, value, row.van_number, row.month, row.year));
      },
      [dispatch, row.month, row.van_number, row.year],
    );

    const defferenceCondition =
      formatNumberType(
        100 *
          Math.abs(
            (Number(rowData.invoice) - rowData.rate) /
              ((Number(rowData.invoice) + rowData.rate) / 2),
          ),
      ) <= 5;
    const disabled = !rowData.rate;
    const cellClass = `${classes.tableCell} ${classes.fixedHeight} ${
      isOffBoarded ? classes.tableCellOffboarded : ""
    } ${isSelected ? classes.activeRow : ""}`;
    const highlightClass = defferenceCondition ? classes.valueOk : classes.valueBad;

    const submitFromEvent = (columnId: string, value: any) => {
      handleUpdateCell(columnId, value);
    };

    return (
      <TableRow hover>
        {isFixed && (
          <TableCell className={cellClass}>
            <Box className={classes.tableCellContent}>
              <Checkbox
                name="selectInvoice"
                checked={isSelected}
                onChange={() =>
                  dispatch(fleetReportsSelectVans(row.van_number, row.month, row.year))
                }
                disabled={disabled}
              />
            </Box>
          </TableCell>
        )}
        {columns.map((col) => (
          <TableCell key={col.id} className={cellClass}>
            <Box className={classes.tableCellContent}>
              {renderFleetCell(
                col.id,
                rowData,
                classes,
                highlightClass,
                disabled,
                isOffBoarded,
                handleChangeCell,
                submitFromEvent,
              )}
            </Box>
          </TableCell>
        ))}
      </TableRow>
    );
  },
  (prev, next) =>
    prev.isFixed === next.isFixed &&
    prev.isOffBoarded === next.isOffBoarded &&
    prev.columns === next.columns &&
    prev.classes === next.classes &&
    isSameFleetRow(prev.row, next.row),
);

const isSameFleetRow = (prev: FleetReportData, next: FleetReportData) => {
  if (prev === next) return true;
  const keys = Object.keys(prev) as (keyof FleetReportData)[];
  return keys.every((key) => prev[key] === next[key]);
};

const formatVanNumberLabel = (
  row: FleetReportData,
  value: string,
) => {
  if (!value) return "---";
  return `${value.split(" - ")[0]}`;
};

const renderFleetCell = (
  columnId: string,
  row: FleetReportData,
  classes: any,
  highlightClass: string,
  disabled: boolean,
  isOffBoarded: boolean | undefined,
  onChange: (columnId: string, value: any) => void,
  onSubmit: (columnId: string, value: any) => void,
) => {
  const value = row[columnId as keyof FleetReportData];

  switch (columnId) {
    case "rate":
      return (
        <Alert
          icon={false}
          className={`${highlightClass} ${classes.cellInput}`}
          style={{ display: "flex", justifyContent: "center", padding: "0 5px" }}
        >
          <Typography>{value}</Typography>
        </Alert>
      );
    case "paid_amount":
    case "deductibles_amount":
    case "deductibles_paid_amount":
      return (
        <TextField
          fullWidth
          variant="outlined"
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(columnId, e.target.value)}
          onBlur={(e) => onSubmit(columnId, Number(e.target.value))}
          disabled={disabled}
        />
      );
    case "invoice_number":
    case "payroll_comment":
      return (
        <TextField
          fullWidth
          variant="outlined"
          value={value ?? ""}
          onChange={(e) => onChange(columnId, e.target.value)}
          onBlur={(e) => onSubmit(columnId, e.target.value)}
          disabled={disabled}
          inputProps={{
            style: {
              minWidth: "224px",
            },
          }}
        />
      );
    case "deductibles_invoice_number":
      return (
        <TextField
          fullWidth
          variant="outlined"
          value={value ?? ""}
          onChange={(e) => onChange(columnId, e.target.value)}
          onBlur={(e) => onSubmit(columnId, Number(e.target.value))}
          disabled={disabled}
        />
      );
    case "invoice_date":
    case "payment_date":
    case "deductibles_payment_date":
    case "rental_period":
    case "rental_period_to":
      return (
        <TextField
          fullWidth
          variant="outlined"
          type="date"
          value={value ?? ""}
          onChange={(e) => onChange(columnId, e.target.value)}
          onBlur={(e) => onSubmit(columnId, e.target.value)}
          disabled={disabled}
        />
      );
    case "payment_method":
    case "deductibles_payment_method":
      return (
        <Select
          fullWidth
          native
          variant="outlined"
          value={value ?? ""}
          onChange={(e) => onSubmit(columnId, e.target.value)}
          disabled={disabled}
        >
          {paymentMethods.map(({ name, value: methodValue }) => (
            <option key={`${name}-${methodValue}`} value={methodValue}>
              {name}
            </option>
          ))}
        </Select>
      );
    case "invoice":
      return (
        <Alert
          icon={false}
          className={`${highlightClass} ${classes.cellInput}`}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Input
            type="number"
            value={value ?? ""}
            onChange={(e) => onChange(columnId, e.target.value)}
            onBlur={(e) => onSubmit(columnId, Number(e.target.value))}
            disabled={disabled}
            disableUnderline
            inputProps={{
              style: {
                padding: 0,
                textAlign: "end",
              },
            }}
          />
        </Alert>
      );
    case "offboarded_date":
      return (
        <Box className={classes.dateCell}>
          {isOffBoarded ? (
            <Tooltip
              title={
                <Typography>{`Provide invoice for Offboarded driver. Located in (${moment()
                  .month(row.month - 1)
                  .format("MMM")}) ${row.month}.${row.year}`}</Typography>
              }
            >
              <Error />
            </Tooltip>
          ) : null}
          <Typography>{value ? moment(value).format("YYYY-MM-DD") : "---"}</Typography>
        </Box>
      );
    case "onboarded_date":
      return <Typography>{value ? moment(value).format("YYYY-MM-DD") : "---"}</Typography>;
    case "van_number":
      return (
        <Typography className={classes.vanNumberText}>
          {formatVanNumberLabel(row, String(value))}
        </Typography>
      );
    case "status":
      return (
        <Typography>
          {value ? (
            <ChipBlock
              text={fleetStatusConstants.find((data) => data.value === value)?.option}
              theme={value === 1 ? "green" : value === 3 ? "gray" : "yellow"}
            />
          ) : (
            ""
          )}
        </Typography>
      );
    default:
      return <Typography>{value ? value : "---"}</Typography>;
  }
};
